import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { editTicketData, getActiveTickets, setAutoClosure } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import Iconify from '../../../../reusable-components/iconify';
import { inXHours } from './RemindMeTimers';
import { markMetricRemoved, NotificationMetricType } from '../../../../redux/slices/notifications';

TicketAutoCompleteActions.propTypes = {
  id: PropTypes.string.isRequired,
  ticketNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  spacing: PropTypes.number,
  navigateAfterAction: PropTypes.bool,
};

export default function TicketAutoCompleteActions({ id, ticketNumber, spacing = 1.5, navigateAfterAction = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = useScopeCheck(['Admin'], true);
  const currentUser = useSelector((state) => state.user.user);
  const { mainTicket, autoClosure, ticketManagers } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};

  const [anchorEl, setAnchorEl] = useState(null);
  const [openAutoCloseDialog, setOpenAutoCloseDialog] = useState(false);
  const [customAutoCloseDate, setCustomAutoCloseDate] = useState(null);

  const ticketId = ticketNumber ?? id;
  const autoClosureId = autoClosure?.id;
  const isManager = ticketManagers?.some(m => m.currentUser);
  const canScheduleComplete = isAdmin || (mainTicket && (currentUser?.email === mainTicket.createdBy || isManager));
  const autoCloseOn = autoClosure?.autoCloseOn && new Date(autoClosure.autoCloseOn) > new Date();

  const closeMenu = () => setAnchorEl(null);

  const resetAutoCloseDialog = () => {
    setOpenAutoCloseDialog(false);
    setCustomAutoCloseDate(null);
  };

  const handleComplete = async (isComplete = true) => {
    const response = await dispatch(editTicketData({ Id: ticketId, value: isComplete, updateType: 'Complete' }));
    if (response) {
      enqueueSnackbar(isComplete ? 'Ticket completed successfully' : 'Ticket completion canceled successfully', { variant: 'success' });
      if (isComplete) {
        dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ticketId));
      }
      if (navigateAfterAction) {
        navigate(`/dashboard/tickets`);
      } else {
        await dispatch(getActiveTickets(false));
      }
    } else {
      enqueueSnackbar('Ticket failed to update', { variant: 'error' });
    }
  };

  const handleAutoClose = async (closeOn, currentAutoClosureId) => {
    const datetime = closeOn instanceof Date ? closeOn : closeOn > 0 ? inXHours(closeOn) : null;
    await dispatch(setAutoClosure(datetime, currentAutoClosureId, ticketId, enqueueSnackbar));
    resetAutoCloseDialog();
  };

  const handleScheduleComplete = async (hours) => {
    closeMenu();
    await handleAutoClose(hours, autoClosureId);
    if (navigateAfterAction) navigate(`/dashboard/tickets`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing, width: '100%' }}>
      {!!autoCloseOn && canScheduleComplete && (
        <AwaitButton
          fullWidth
          variant="outlined"
          size="small"
          color="error"
          onClick={async () => {
            await handleAutoClose(null, autoClosureId);
          }}
        >
          Cancel Autocompletion
        </AwaitButton>
      )}
      {mainTicket?.complete && (
        <AwaitButton
          fullWidth
          variant="outlined"
          size="small"
          color="secondary"
          onClick={() => handleComplete(false)}
        >
          Reopen ticket
        </AwaitButton>
      )}
      {!mainTicket?.complete && !autoCloseOn && (
        canScheduleComplete ? (
          <ButtonGroup fullWidth variant="outlined" color="secondary" size="small">
            <AwaitButton sx={{ flexGrow: 1 }} onClick={() => handleComplete(true)}>
              Complete Ticket
            </AwaitButton>
            <Button
              sx={{ width: '42px', minWidth: '42px', px: 0, flexGrow: 0, bgcolor: anchorEl ? 'action.hover' : 'transparent' }}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <Iconify icon={anchorEl ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={18} height={18} />
            </Button>
          </ButtonGroup>
        ) : (
          <AwaitButton
            fullWidth
            variant="outlined"
            size="small"
            color="secondary"
            onClick={() => handleComplete(true)}
          >
            Complete Ticket
          </AwaitButton>
        )
      )}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleScheduleComplete(24)}>Complete in 24 hours</MenuItem>
        <MenuItem onClick={() => handleScheduleComplete(48)}>Complete in 48 hours</MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            setOpenAutoCloseDialog(true);
          }}
        >
          Set custom date
        </MenuItem>
      </Menu>
      <Dialog open={openAutoCloseDialog} onClose={resetAutoCloseDialog}>
        <DialogTitle>Schedule Ticket Completion</DialogTitle>
        <DialogContent>
          <DateTimePicker
            sx={{ width: '100%', mt: 1 }}
            closeOnSelect={false}
            minDateTime={new Date()}
            slotProps={{
              textField: { size: 'small' },
              actionBar: {
                actions: ['clear', 'cancel', 'accept'],
                acceptButtonProps: {
                  disabled: !customAutoCloseDate || (customAutoCloseDate && customAutoCloseDate < new Date()),
                },
              },
            }}
            value={customAutoCloseDate}
            onChange={(newValue) => {
              setCustomAutoCloseDate(newValue);
            }}
            onAccept={async (dateValue) => {
              setCustomAutoCloseDate(dateValue);
              await handleAutoClose(dateValue, autoClosureId);
              if (navigateAfterAction) navigate(`/dashboard/tickets`);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAutoCloseDialog} color="secondary">Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
